"use client";

import React from "react";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  TooltipContent,
  TooltipTrigger,
  TruncateTooltip,
} from "@/components/ui/truncate-tooltip";

type SitemapItem = {
  url: string;
  type?: "index" | "regular";
  parentUrl?: string;
};

type AutocompleteSitemapInputProps = {
  value: string;
  onChange: (value: string) => void;
  sitemaps: SitemapItem[];
  placeholder?: string;
  id?: string;
  required?: boolean;
};

export function AutocompleteSitemapInput({
  value,
  onChange,
  sitemaps,
  placeholder,
  id: propId,
  required,
}: AutocompleteSitemapInputProps) {
  const [filteredSitemaps, setFilteredSitemaps] = React.useState<SitemapItem[]>(
    [],
  );
  const [showSuggestions, setShowSuggestions] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const suggestionRefs = React.useRef<(HTMLLIElement | null)[]>([]);
  const inputRef = React.useRef<HTMLInputElement>(null);
  const componentId = React.useId();
  const inputId = propId || `${componentId}-autocomplete-sitemap`;

  React.useEffect(() => {
    suggestionRefs.current = suggestionRefs.current.slice(
      0,
      filteredSitemaps.length,
    );
  }, [filteredSitemaps]);

  React.useEffect(() => {
    if (activeIndex !== -1 && suggestionRefs.current[activeIndex]) {
      suggestionRefs.current[activeIndex]?.scrollIntoView({
        block: "nearest",
      });
    }
  }, [activeIndex]);

  const filterAndShowSuggestions = (inputValue: string) => {
    const newFiltered = sitemaps.filter((sitemap) =>
      sitemap.url.toLowerCase().includes(inputValue.toLowerCase()),
    );
    setFilteredSitemaps(newFiltered);
    setShowSuggestions(newFiltered.length > 0);
  };

  const handleInputChange = (inputValue: string) => {
    onChange(inputValue);
    setActiveIndex(-1);
    if (inputValue) {
      filterAndShowSuggestions(inputValue);
    } else {
      setFilteredSitemaps(sitemaps);
      setShowSuggestions(sitemaps.length > 0);
    }
  };

  const showInitialSuggestionsOnFocus = () => {
    setActiveIndex(-1);
    setFilteredSitemaps(sitemaps);
    setShowSuggestions(sitemaps.length > 0);
  };

  const handleSuggestionClick = (sitemap: SitemapItem) => {
    onChange(sitemap.url);
    setShowSuggestions(false);
    setActiveIndex(-1);
    inputRef.current?.focus();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (showSuggestions && filteredSitemaps.length > 0) {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setActiveIndex((prevIndex) =>
          prevIndex === filteredSitemaps.length - 1 ? 0 : prevIndex + 1,
        );
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setActiveIndex((prevIndex) =>
          prevIndex <= 0 ? filteredSitemaps.length - 1 : prevIndex - 1,
        );
      } else if (event.key === "Enter" && activeIndex >= 0) {
        event.preventDefault();
        const selectedSitemap = filteredSitemaps[activeIndex];
        if (selectedSitemap) {
          handleSuggestionClick(selectedSitemap);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        setShowSuggestions(false);
        setActiveIndex(-1);
      }
    } else if (event.key === "ArrowDown" || event.key === "ArrowUp") {
      if (!showSuggestions && sitemaps.length > 0) {
        event.preventDefault();
        showInitialSuggestionsOnFocus();
        setActiveIndex(
          event.key === "ArrowDown" ? 0 : filteredSitemaps.length - 1,
        );
      }
    }
  };

  return (
    <div className="relative w-full">
      <Input
        ref={inputRef}
        id={inputId}
        type="url"
        value={value}
        onChange={(e) => handleInputChange(e.target.value)}
        onFocus={showInitialSuggestionsOnFocus}
        onBlur={() =>
          setTimeout(() => {
            if (
              !suggestionRefs.current.some(
                (ref) => ref === document.activeElement?.closest("li"),
              )
            ) {
              setShowSuggestions(false);
            }
          }, 150)
        }
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        required={required}
        className="w-full"
      />
      {showSuggestions && filteredSitemaps.length > 0 && (
        <div className="absolute z-10 w-full bg-background border border-border shadow-lg rounded-md mt-1 top-full">
          <ScrollArea className="max-h-64">
            <ul
              className="text-sm"
              role="listbox"
              aria-activedescendant={
                activeIndex >= 0
                  ? `${inputId}-suggestion-${activeIndex}`
                  : undefined
              }
            >
              {filteredSitemaps.map((sitemap, index) => (
                <li
                  key={sitemap.url}
                  id={`${inputId}-suggestion-${index}`}
                  ref={(el) => {
                    suggestionRefs.current[index] = el;
                  }}
                  role="option"
                  aria-selected={activeIndex === index}
                  className={`px-3 py-2 cursor-pointer ${
                    activeIndex === index ? "bg-accent" : "hover:bg-accent"
                  } ${sitemap.parentUrl ? "pl-6" : ""}`}
                  onClick={() => handleSuggestionClick(sitemap)}
                >
                  <TruncateTooltip>
                    <TooltipTrigger asChild>
                      <div className="w-full min-w-0 line-clamp-2 break-all">
                        {sitemap.parentUrl && (
                          <span className="text-muted-foreground mr-1">└</span>
                        )}
                        {sitemap.url}
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="max-w-[600px] break-all"
                    >
                      <p>{sitemap.url}</p>
                    </TooltipContent>
                  </TruncateTooltip>
                </li>
              ))}
            </ul>
          </ScrollArea>
        </div>
      )}
    </div>
  );
}
